import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Overline } from "../components/Overline";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { RESOURCES } from "../lib/site";
import { Seo } from "../components/Seo";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api
      .get("/blog")
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const requestDownload = (resource) => {
    setActive(resource);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !active) return;
    setSending(true);
    try {
      await api.post("/resources/download", {
        email,
        name: name || null,
        resource_slug: active.slug,
      });
      toast.success(`"${active.title}" is on its way to your inbox.`);
      setOpen(false);
      setName("");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-blog" className="bg-[#faf9f6]">
      <Seo title="Articles" description="Research and thoughts on the latest in women's health. PCOS, perimenopause, the pill, cycle tracking, DUTCH testing and more." path="/articles" />
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="max-w-3xl">
          <Overline>Articles & Free Guides</Overline>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.04] font-normal text-[#333] mt-5">
            Research and thoughts
            <br />
            <span className="italic">on the latest in women's health.</span>
          </h1>
        </div>

        {/* FREE GUIDES STRIP */}
        <div className="mt-20">
          <div className="flex items-end justify-between gap-6 mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#5fa292]">
                Free resources
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#333] mt-3 font-normal tracking-tight">
                Beautifully designed guides to download.
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {RESOURCES.map((r) => (
              <article
                key={r.slug}
                data-testid={`resource-${r.slug}`}
                className="bg-white border border-[#e5e0d8] rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#5fa292]/5 flex flex-col"
              >
                <div className="aspect-[16/10] bg-[#cbdad6] overflow-hidden">
                  <img src={r.image} alt={r.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#5fa292]">{r.pages}</p>
                  <h3 className="font-serif text-xl text-[#333] mt-2 font-normal tracking-tight leading-snug">
                    {r.title}
                  </h3>
                  <p className="mt-3 text-[#3d3d3d] font-normal leading-relaxed text-sm flex-1">
                    {r.description}
                  </p>
                  <Button
                    data-testid={`resource-download-${r.slug}`}
                    onClick={() => requestDownload(r)}
                    className="mt-5 bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full h-10 w-full text-sm"
                  >
                    <Download size={14} className="mr-2" />
                    Get it free
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* ARTICLES */}
        <div className="mt-24">
          <Overline>Articles</Overline>
          <h2 className="font-serif text-3xl md:text-4xl text-[#333] mt-3 font-normal tracking-tight">
            Recent essays from the practice.
          </h2>

          {loading ? (
            <p data-testid="blog-loading" className="mt-16 text-center text-[#3d3d3d] font-normal">
              Loading…
            </p>
          ) : posts.length === 0 ? (
            <p className="mt-16 text-center text-[#3d3d3d] font-normal">
              Articles coming soon.
            </p>
          ) : (
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/articles/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group block bg-white border border-[#e5e0d8] rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#5fa292]/5"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[#cbdad6]">
                    <img
                      src={p.cover_image}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-7">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#5fa292]">
                      {p.category} · {p.read_time}
                    </p>
                    <h2 className="font-serif text-2xl md:text-[26px] text-[#333] mt-3 font-normal tracking-tight leading-snug">
                      {p.title}
                    </h2>
                    <p className="mt-3 text-[#3d3d3d] font-normal leading-relaxed text-sm">
                      {p.excerpt}
                    </p>
                    <p className="mt-5 inline-flex items-center gap-2 text-sm text-[#5fa292] font-medium">
                      Read article <ArrowRight size={14} />
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="resource-dialog" className="bg-[#faf9f6] border-[#e5e0d8] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl font-normal text-[#333] tracking-tight">
              {active?.title}
            </DialogTitle>
            <DialogDescription className="text-[#3d3d3d] font-normal">
              Where shall I send it?
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} data-testid="resource-form" className="space-y-4 mt-2">
            <Input
              placeholder="First name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="resource-name-input"
              className="bg-white"
            />
            <Input
              type="email"
              required
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="resource-email-input"
              className="bg-white"
            />
            <Button
              type="submit"
              disabled={sending}
              data-testid="resource-submit"
              className="w-full bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full h-11"
            >
              {sending ? "Sending..." : "Send me the guide"}
            </Button>
            <p className="text-xs text-[#3d3d3d] text-center font-normal">
              I'll never share your email. Unsubscribe in one click.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
